import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import CommonDataGrid from '../../Stateless/CommonDataGrid';
import { useGetAllJoinUs } from '../../Query/JoinUsQuery';
import { useManageJoinUs } from './useManageJoinUs';
import { useDebounce } from '../Utils/useDounce';

type JoinUsEntry = {
  id: string;
  name: string;
  email: string;
  phone: string;
  type: 'Rider' | 'Partner';
  vehicle?: 'Two-wheeler' | 'Four-wheeler' | 'Both';
  contactPerson?: string;
  createdAt: string;
};

const JoinUsList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [selectedEntry, setSelectedEntry] = useState<JoinUsEntry | null>(null);
  const [openModal, setOpenModal] = useState(false);

  const { params, dispatch } = useManageJoinUs();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);

  const { data: joinUsList, isLoading } = useGetAllJoinUs(memoizedParams);

  const filteredRows = useMemo(() => {
    if (!joinUsList?.data?.joinUsEntries) return [];
    return joinUsList.data.joinUsEntries.map((row: any, index: number) => {
      const mappedRow = {
        ...row,
        id: row.id || `joinus-${index}`,
        serialNumber: index + 1 + (Number(params.page - 1)) * Number(params.limit),
        name: row.name || '',
        email: row.email || '',
        phone: row.phone || '',
        type: row.type || 'Rider',
        vehicle: row.vehicle,
        contactPerson: row.contactPerson,
        created_at: row.createdAt || row.created_at || '',
      };
      return mappedRow;
    });
  }, [params.search, joinUsList, params.page, params.limit]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  const handleTypeFilterChange = (event: any) => {
    const value = event.target.value === 'all' ? undefined : event.target.value;
    dispatch({ type: 'SET_TYPE_FILTER', payload: value });
  };

  const handleViewDetails = (row: JoinUsEntry) => {
    setSelectedEntry(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedEntry(null);
  };

  // Define columns for the data grid
  const columns = [
    {
      field: 'serialNumber',
      headerName: 'S.No',
      width: 80,
      sortable: false,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
    },
    
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      width: 100,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      minWidth: 120,
      renderCell: (params: any) => (
        <Chip
          label={params.value}
          color={params.value === 'Rider' ? 'primary' : 'secondary'}
          size="small"
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 200,
      valueFormatter: (params: any) => {
        if (!params) return '-';
        try {
          return new Date(params).toLocaleString();
        } catch {
          return params;
        }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: any) => (
        <IconButton
          onClick={() => handleViewDetails(params.row)}
          size="small"
          color="secondary"
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name, email, or phone"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ flex: '0 0 200px' }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Type</InputLabel>
              <Select
                value={params.type || 'all'}
                onChange={handleTypeFilterChange}
                label="Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="Rider">Rider</MenuItem>
                <MenuItem value="Partner">Partner</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <CommonDataGrid
        rows={filteredRows}
        columns={columns}
        pageSize={params.limit}
        onPageSizeChange={handlePageSizeChange}
        checkboxSelection={false}
        loading={isLoading}
        page={params.page - 1}
        rowCount={joinUsList?.data?.pagination?.totalItems || 0}
        onPageChange={handlePageChange}
        components={{}}
      />

      {joinUsList?.data?.pagination && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            Showing {filteredRows.length} of {joinUsList.data.pagination.totalItems} entries
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Page {joinUsList.data.pagination.currentPage} of {joinUsList.data.pagination.totalPages}
          </Typography>
        </Box>
      )}

      {/* Details Modal */}
       <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Join Us Request Details</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedEntry && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                  Name:
                </Typography>
                <Typography variant="body1">{selectedEntry.name || '-'}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                  Email:
                </Typography>
                <Typography variant="body1">{selectedEntry.email || '-'}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                  Phone:
                </Typography>
                <Typography variant="body1">{selectedEntry.phone || '-'}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                  Type:
                </Typography>
                <Chip
                  label={selectedEntry.type}
                  color={selectedEntry.type === 'Rider' ? 'primary' : 'secondary'}
                  size="small"
                />
              </Box>
              
              {selectedEntry.vehicle && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                    Vehicle:
                  </Typography>
                  <Typography variant="body1">{selectedEntry.vehicle}</Typography>
                </Box>
              )}

              {selectedEntry.contactPerson && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                    Contact Person:
                  </Typography>
                  <Typography variant="body1">{selectedEntry.contactPerson}</Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                  Created At:
                </Typography>
                <Typography variant="body1">
                  {selectedEntry.createdAt 
                    ? new Date(selectedEntry.createdAt).toLocaleString() 
                    : '-'}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={handleCloseModal} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JoinUsList;